import React, { useRef, useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * ScrollableTabBar
 * Horizontal drag-to-scroll container with fade-in left/right arrow buttons
 * that appear ONLY when the pointer is NOT hovering the bar and the bar is
 * actually scrollable in that direction. Wheel + shift+wheel + touch swipe
 * work natively. Drag distance threshold prevents accidental click hijacks
 * on child tab buttons.
 */
const ScrollableTabBar = ({ children, className = '', testId = 'scrollable-tab-bar' }) => {
  const scrollRef = useRef(null);
  const dragRef = useRef({
    isDown: false,
    startX: 0,
    scrollLeft: 0,
    moved: 0,
  });
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  const updateEdges = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const tolerance = 8;
    setCanLeft(el.scrollLeft > tolerance);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - tolerance);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Measure now, next frame, and after a short delay to catch async child
    // rendering (icons, badges) and font-loading layout shifts.
    updateEdges();
    const raf = requestAnimationFrame(updateEdges);
    const t1 = setTimeout(updateEdges, 100);
    const t2 = setTimeout(updateEdges, 500);
    const onScroll = () => updateEdges();
    el.addEventListener('scroll', onScroll, { passive: true });
    const onResize = () => updateEdges();
    window.addEventListener('resize', onResize);
    // Re-check when children change (tabs may render async)
    const mo = new MutationObserver(updateEdges);
    mo.observe(el, { childList: true, subtree: true });
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t1);
      clearTimeout(t2);
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      mo.disconnect();
    };
  }, [updateEdges]);

  const scrollByAmount = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = Math.max(160, Math.floor(el.clientWidth * 0.7));
    el.scrollBy({ left: dir * amount, behavior: 'smooth' });
  };

  const handleMouseDown = (e) => {
    const el = scrollRef.current;
    if (!el) return;
    dragRef.current.isDown = true;
    dragRef.current.startX = e.pageX - el.offsetLeft;
    dragRef.current.scrollLeft = el.scrollLeft;
    dragRef.current.moved = 0;
    el.classList.add('cursor-grabbing');
  };

  const handleMouseLeave = () => {
    dragRef.current.isDown = false;
    const el = scrollRef.current;
    if (el) el.classList.remove('cursor-grabbing');
    setIsHovering(false);
  };

  const handleMouseUp = () => {
    dragRef.current.isDown = false;
    const el = scrollRef.current;
    if (el) el.classList.remove('cursor-grabbing');
  };

  const handleMouseMove = (e) => {
    if (!dragRef.current.isDown) return;
    const el = scrollRef.current;
    if (!el) return;
    e.preventDefault();
    const x = e.pageX - el.offsetLeft;
    const walk = x - dragRef.current.startX;
    dragRef.current.moved = Math.max(dragRef.current.moved, Math.abs(walk));
    el.scrollLeft = dragRef.current.scrollLeft - walk;
  };

  // Suppress click if the user actually dragged (>5px). We capture on the
  // bubble phase and stop propagation before child onClick fires.
  const handleClickCapture = (e) => {
    if (dragRef.current.moved > 5) {
      e.preventDefault();
      e.stopPropagation();
      dragRef.current.moved = 0;
    }
  };

  return (
    <div
      className="relative"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      data-testid={`${testId}-wrap`}
    >
      <div
        ref={scrollRef}
        className={`flex overflow-x-auto no-scrollbar snap-x cursor-grab select-none touch-pan-x ${className}`}
        style={{ WebkitOverflowScrolling: 'touch' }}
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseLeave}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
        onClickCapture={handleClickCapture}
        data-testid={testId}
      >
        {children}
      </div>

      {/* Left edge fade + arrow - Fade extends further out to reveal the first tab. Arrow is pushed 18px outward. */}
      <div
        className={`pointer-events-none absolute -left-[18px] top-0 bottom-0 w-24 bg-gradient-to-r from-gray-900 via-gray-900/60 to-transparent transition-opacity duration-200 ${
          canLeft ? 'opacity-100' : 'opacity-0'
        }`}
        aria-hidden="true"
      />
      <button
        type="button"
        aria-label="Scroll venstre"
        onClick={() => scrollByAmount(-1)}
        tabIndex={canLeft ? 0 : -1}
        className={`flex absolute -left-[18px] top-1/2 -translate-y-1/2 items-center justify-center w-6 h-6 rounded-full bg-white/60 text-gray-800 shadow ring-1 ring-white/40 hover:bg-white/90 transition-opacity duration-200 z-10 ${
          canLeft ? 'opacity-90' : 'opacity-0 pointer-events-none'
        }`}
        data-testid={`${testId}-arrow-left`}
      >
        <ChevronLeft className="w-4 h-4" />
      </button>

      {/* Right edge fade + arrow */}
      <div
        className={`pointer-events-none absolute -right-[18px] top-0 bottom-0 w-24 bg-gradient-to-l from-gray-900 via-gray-900/60 to-transparent transition-opacity duration-200 ${
          canRight ? 'opacity-100' : 'opacity-0'
        }`}
        aria-hidden="true"
      />
      <button
        type="button"
        aria-label="Scroll høyre"
        onClick={() => scrollByAmount(1)}
        tabIndex={canRight ? 0 : -1}
        className={`flex absolute -right-[18px] top-1/2 -translate-y-1/2 items-center justify-center w-6 h-6 rounded-full bg-white/60 text-gray-800 shadow ring-1 ring-white/40 hover:bg-white/90 transition-opacity duration-200 z-10 ${
          canRight ? 'opacity-90' : 'opacity-0 pointer-events-none'
        }`}
        data-testid={`${testId}-arrow-right`}
      >
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
};

export default ScrollableTabBar;
